/* eslint-disable default-case */
/* eslint-disable no-new-func */
import React, { useState, useEffect, useRef } from "react";
import { useHistoryContext } from "../context/HistoryContext";
import safeLogger from "../utils/safeLogger";
import {
  Container,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Paper,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Controlled as CodeMirror } from "react-codemirror2";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useThemeContext } from "../components/GeneralConfig"; // Integração com tema

import "codemirror/lib/codemirror.css";
import "codemirror/theme/material.css";
import "codemirror/mode/javascript/javascript";
import "codemirror/mode/python/python";
import "codemirror/mode/clike/clike";
import api from "../api";

const UnitTests = () => {
  const [language, setLanguage] = useState("");
  const [code, setCode] = useState("");
  const [result, setResult] = useState("");
  const [isError, setIsError] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copyMessage, setCopyMessage] = useState("");
  const [suggestedFileName, setSuggestedFileName] = useState("");
  const resultRef = useRef(null); // Para rolagem automática
  const { darkMode } = useThemeContext(); // Tema atual
  const { addToHistory } = useHistoryContext();
  const resultBoxRef = useRef(null);

  const instructions = {
    JavaScript: (
      <>
        <Typography variant="body2" gutterBottom>
          Siga os passos abaixo para configurar testes unitários com Jest:
        </Typography>
        <ol>
          <li>
            <strong>Instale o Jest:</strong>
            <pre
              style={{
                backgroundColor: darkMode ? "#1d1d1d" : "#f9f9f9",
                color: darkMode ? "#ffffff" : "#000000",
                padding: "8px",
                borderRadius: "4px",
                border: `1px solid ${darkMode ? "#333" : "#ccc"}`,
              }}
            >
              npm install --save-dev jest
            </pre>
          </li>
          <li>
            <strong>Configure o Jest:</strong>
            <Typography variant="body2">
              Crie um arquivo <code>jest.config.js</code> na raiz do seu projeto
              com o seguinte conteúdo:
            </Typography>
            <pre
              style={{
                backgroundColor: darkMode ? "#1d1d1d" : "#f9f9f9",
                color: darkMode ? "#ffffff" : "#000000",
                padding: "8px",
                borderRadius: "4px",
                border: `1px solid ${darkMode ? "#333" : "#ccc"}`,
              }}
            >
              {`module.exports = {
  testEnvironment: "node",
};`}
            </pre>
          </li>
          <li>
            <strong>Crie o arquivo de teste:</strong>
            <Typography variant="body2">
              Salve o código gerado em um arquivo com extensão{" "}
              <code>.test.js</code>, por exemplo:
            </Typography>
            <pre
              style={{
                backgroundColor: darkMode ? "#1d1d1d" : "#f9f9f9",
                color: darkMode ? "#ffffff" : "#000000",
                padding: "8px",
                borderRadius: "4px",
                border: `1px solid ${darkMode ? "#333" : "#ccc"}`,
              }}
            >
              tests/myFunction.test.js
            </pre>
          </li>
          <li>
            <strong>Execute os testes:</strong>
            <pre
              style={{
                backgroundColor: darkMode ? "#1d1d1d" : "#f9f9f9",
                color: darkMode ? "#ffffff" : "#000000",
                padding: "8px",
                borderRadius: "4px",
                border: `1px solid ${darkMode ? "#333" : "#ccc"}`,
              }}
            >
              npm test
            </pre>
          </li>
        </ol>
      </>
    ),
    Python: (
      <>
        <Typography variant="body2" gutterBottom>
          Siga os passos abaixo para configurar testes unitários com unittest:
        </Typography>
        <ol>
          <li>
            <strong>Crie um arquivo de teste:</strong>
            <Typography variant="body2">
              Salve o código gerado em um arquivo com o prefixo{" "}
              <code>test_</code>, por exemplo:
            </Typography>
            <pre
              style={{
                backgroundColor: darkMode ? "#1d1d1d" : "#f9f9f9",
                color: darkMode ? "#ffffff" : "#000000",
                padding: "8px",
                borderRadius: "4px",
                border: `1px solid ${darkMode ? "#333" : "#ccc"}`,
              }}
            >
              tests/test_my_function.py
            </pre>
          </li>
          <li>
            <strong>Execute os testes:</strong>
            <pre
              style={{
                backgroundColor: darkMode ? "#1d1d1d" : "#f9f9f9",
                color: darkMode ? "#ffffff" : "#000000",
                padding: "8px",
                borderRadius: "4px",
                border: `1px solid ${darkMode ? "#333" : "#ccc"}`,
              }}
            >
              python -m unittest discover -s tests
            </pre>
          </li>
        </ol>
      </>
    ),
    Java: (
      <>
        <Typography variant="body2" gutterBottom>
          Siga os passos abaixo para configurar testes unitários com JUnit:
        </Typography>
        <ol>
          <li>
            <strong>Adicione a dependência do JUnit ao seu projeto:</strong>
            <pre
              style={{
                backgroundColor: darkMode ? "#1d1d1d" : "#f9f9f9",
                color: darkMode ? "#ffffff" : "#000000",
                padding: "8px",
                borderRadius: "4px",
                border: `1px solid ${darkMode ? "#333" : "#ccc"}`,
              }}
            >
              {`<dependency>
  <groupId>junit</groupId>
  <artifactId>junit</artifactId>
  <version>4.13.2</version>
  <scope>test</scope>
</dependency>`}
            </pre>
          </li>
          <li>
            <strong>Crie o arquivo de teste:</strong>
            <Typography variant="body2">
              Salve o código gerado em um arquivo com o sufixo{" "}
              <code>Test.java</code>, por exemplo:
            </Typography>
            <pre
              style={{
                backgroundColor: darkMode ? "#1d1d1d" : "#f9f9f9",
                color: darkMode ? "#ffffff" : "#000000",
                padding: "8px",
                borderRadius: "4px",
                border: `1px solid ${darkMode ? "#333" : "#ccc"}`,
              }}
            >
              src/test/java/MyFunctionTest.java
            </pre>
          </li>
          <li>
            <strong>Execute os testes:</strong>
            <Typography variant="body2">Use o comando do Maven:</Typography>
            <pre
              style={{
                backgroundColor: darkMode ? "#1d1d1d" : "#f9f9f9",
                color: darkMode ? "#ffffff" : "#000000",
                padding: "8px",
                borderRadius: "4px",
                border: `1px solid ${darkMode ? "#333" : "#ccc"}`,
              }}
            >
              mvn test
            </pre>
          </li>
        </ol>
      </>
    ),
    "C#": (
      <>
        <Typography variant="body2" gutterBottom>
          Siga os passos abaixo para configurar testes unitários com NUnit:
        </Typography>
        <ol>
          <li>
            <strong>Adicione o NUnit ao seu projeto:</strong>
            <pre
              style={{
                backgroundColor: darkMode ? "#1d1d1d" : "#f9f9f9",
                color: darkMode ? "#ffffff" : "#000000",
                padding: "8px",
                borderRadius: "4px",
                border: `1px solid ${darkMode ? "#333" : "#ccc"}`,
              }}
            >
              dotnet add package NUnit --version 3.13.3
            </pre>
          </li>
          <li>
            <strong>Crie o arquivo de teste:</strong>
            <Typography variant="body2">
              Salve o código gerado em um arquivo com o sufixo{" "}
              <code>Tests.cs</code>, por exemplo:
            </Typography>
            <pre
              style={{
                backgroundColor: darkMode ? "#1d1d1d" : "#f9f9f9",
                color: darkMode ? "#ffffff" : "#000000",
                padding: "8px",
                borderRadius: "4px",
                border: `1px solid ${darkMode ? "#333" : "#ccc"}`,
              }}
            >
              MyFunctionTests.cs
            </pre>
          </li>
          <li>
            <strong>Execute os testes:</strong>
            <pre
              style={{
                backgroundColor: darkMode ? "#1d1d1d" : "#f9f9f9",
                color: darkMode ? "#ffffff" : "#000000",
                padding: "8px",
                borderRadius: "4px",
                border: `1px solid ${darkMode ? "#333" : "#ccc"}`,
              }}
            >
              dotnet test
            </pre>
          </li>
        </ol>
      </>
    ),
  };

  useEffect(() => {
    if (resultRef.current) {
      resultRef.current.scrollTop = resultRef.current.scrollHeight;
    }
  }, [result]);

  // Sugere o nome do arquivo com base na linguagem
  const suggestFileName = (lang) => {
    const map = {
      JavaScript: "meu_codigo.test.js",
      Python: "test_meu_codigo.py",
      Java: "MeuCodigoTest.java",
      "C#": "MeuCodigoTests.cs",
    };
    return map[lang] || "tests";
  };

  const handleConversion = async () => {
    if (!language || !code.trim()) {
      alert("Por favor, insira um código e selecione a linguagem.");
      return;
    }

    setIsLoading(true);
    setResult("");
    setIsError(false);
    setSuggestedFileName("");

    try {
      const response = await api.post("/api/generate-tests", {
        code,
        language,
      });

      // Limpa possíveis "undefined" que o backend pode concatenar por engano
      const generatedTest = (
        response.data?.tests ||
        response.data?.result ||
        response.data?.generatedTest ||
        ""
      ).replace(/undefined$/g, "").trimEnd();

      if (!generatedTest) {
        setResult("Nenhum teste foi retornado pelo servidor.");
        return;
      }

      setSuggestedFileName(suggestFileName(language));
      typeEffect(generatedTest);

      addToHistory({
        date: new Date().toISOString(),
        type: `Testes Unitários (${language})`,
        details: `Código convertido para testes unitários em ${language}`,
      });
    } catch (error) {
      safeLogger.error("[UnitTests] Erro ao gerar testes:", error.message);
      let msg;
      if (!error.response) {
        msg =
          "Não foi possível conectar ao servidor (porta 3000). Certifique-se de que o backend está rodando: npm start (no projeto do servidor).";
      } else {
        const status = error.response.status;
        const serverMsg =
          error.response.data?.error ||
          error.response.data?.message ||
          `Erro ${status}`;
        msg = `Servidor retornou ${status}: ${serverMsg}`;
      }
      setIsError(true);
      setResult(
        `<div style="background-color:#f8f9fa;padding:8px;border:1px solid #d0d7de;border-radius:8px;font-family:'Courier New',monospace;font-size:14px;line-height:1.5;color:#333;">
          <p style="color:#d32f2f;font-weight:bold;margin:0;">Erro: ${msg}</p>
          <p style="margin:8px 0 0;">Por favor, corrija os problemas e tente novamente.<br/>
            <a href="/code-analysis" style="color:#1d4f91;text-decoration:underline;">Clique aqui para analisar e corrigir o código.</a>
          </p>
        </div>`
      );
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const _generateUnitTest_DEPRECATED = (inputCode, lang) => {
    switch (lang) {
      case "JavaScript": {
        // Validação e geração para JavaScript
        const isJavaScriptValid = (code) => {
          try {
            new Function(code); // Valida se o código é válido
            return true;
          } catch (error) {
            return false;
          }
        };

        if (!isJavaScriptValid(inputCode)) {
          return `
            <div style="background-color: #f8f9fa; padding: 8px; border: 1px solid #d0d7de; border-radius: 8px; font-family: 'Courier New', Courier, monospace; font-size: 14px; line-height: 1.5; color: #333;">
              <p style="color: #d32f2f; font-weight: bold; margin: 0;">
                Erro: Código JavaScript não está estruturado corretamente.
              </p>
              <p style="margin: 8px 0 0;">
                Por favor, corrija os problemas estruturais antes de tentar novamente.
              </p>
            </div>
          `;
        }

        try {
          const functionRegex = /function\s+(\w+)\s*\((.*?)\)\s*{/g;
          const matches = Array.from(inputCode.matchAll(functionRegex));
          const tests = matches.map((match) => {
            const functionName = match[1];
            const params = match[2]
              .split(",")
              .map((p) => p.trim())
              .filter(Boolean);

            const paramMocks = params
              .map((_param, index) => `mockParam${index + 1}`)
              .join(", ");

            return `
              it('Testa a função ${functionName}', () => {
                const result = ${functionName}(${paramMocks});
                expect(result).toBe(/* Resultado esperado */);
              });
            `;
          });

          if (tests.length === 0) {
            return `
              describe('Unit Tests', () => {
                it('Nenhuma função detectada. Adicione mais detalhes ao código.', () => {
                  expect(true).toBe(true);
                });
              });
            `;
          }

          return `
            describe('Unit Tests', () => {
              ${tests.join("\n")}
            });
          `;
        } catch (error) {
          return "Erro ao gerar testes unitários.";
        }
      }

      case "Python": {
        const isPythonValid = (code) => {
          try {
            const hasDefKeyword = /def\s+\w+\s*\(.*?\)\s*:/.test(code); // Verifica se há funções definidas
            const hasIndentation = /^\s{4}/m.test(code); // Verifica se há indentação
            const unmatchedBraces =
              (code.match(/\(/g)?.length || 0) !==
              (code.match(/\)/g)?.length || 0); // Verifica parênteses desbalanceados

            if (!hasDefKeyword || unmatchedBraces || !hasIndentation) {
              return false;
            }
            return true;
          } catch (error) {
            return false;
          }
        };

        if (!isPythonValid(inputCode)) {
          return `
            <div style="background-color: #f8f9fa; padding: 8px; border: 1px solid #d0d7de; border-radius: 8px; font-family: 'Courier New', Courier, monospace; font-size: 14px; line-height: 1.5; color: #333;">
              <p style="color: #d32f2f; font-weight: bold; margin: 0;">
                Erro: Código Python não está estruturado corretamente.
              </p>
              <p style="margin: 8px 0 0;">
                Por favor, corrija os problemas estruturais antes de tentar novamente.<br/>
                <a href="/code-analysis" style="color: #1d4f91; text-decoration: underline;">
                  Clique aqui para analisar e corrigir o código.
                </a>
              </p>
            </div>
          `;
        }

        try {
          const functionRegex = /def\s+(\w+)\s*\((.*?)\)\s*:/g;
          const matches = Array.from(inputCode.matchAll(functionRegex));
          const tests = matches.map((match) => {
            const functionName = match[1];
            const params = match[2]
              .split(",")
              .map((p) => p.trim())
              .filter(Boolean);

            const paramMocks = params
              .map((param, index) => `mock_param${index + 1}`)
              .join(", ");

            return `
              def test_${functionName}(self):
                  result = ${functionName}(${paramMocks})
                  self.assertEqual(result, /* Resultado esperado */)
            `;
          });

          if (tests.length === 0) {
            return `
              <div style="background-color: #f8f9fa; padding: 8px; border: 1px solid #d0d7de; border-radius: 8px; font-family: 'Courier New', Courier, monospace; font-size: 14px; line-height: 1.5; color: #333;">
                <p style="color: #d32f2f; font-weight: bold; margin: 0;">
                  Erro: Código Python não contém funções detectáveis.
                </p>
                <p style="margin: 8px 0 0;">
                  Por favor, revise o código e adicione funções antes de tentar novamente.
                </p>
              </div>
            `;
          }

          return `
            import unittest
      
            class GeneratedTests(unittest.TestCase):
              ${tests.join("\n")}
      
            if __name__ == "__main__":
                unittest.main()
          `;
        } catch (error) {
          return `
            <div style="background-color: #f8f9fa; padding: 8px; border: 1px solid #d0d7de; border-radius: 8px; font-family: 'Courier New', Courier, monospace; font-size: 14px; line-height: 1.5; color: #333;">
              <p style="color: #d32f2f; font-weight: bold; margin: 0;">
                Erro: Ocorreu um problema ao processar o código Python.
              </p>
            </div>
          `;
        }
      }

      case "Java": {
        // Função para validar a estrutura do código Java
        const isJavaValid = (code) => {
          try {
            // Verifica se há uma classe
            const hasClass = /class\s+\w+\s*\{/.test(code);
            // Verifica se há pelo menos um método público
            const hasMethods = /public\s+\w+\s+\w+\s*\(.*\)\s*\{/.test(code);
            // Verifica se as chaves estão balanceadas
            const openBraces = (code.match(/{/g) || []).length;
            const closeBraces = (code.match(/}/g) || []).length;

            if (!hasClass) {
              return false;
            }
            if (!hasMethods) {
              return false;
            }
            if (openBraces !== closeBraces) {
              return false;
            }
            return true;
          } catch (error) {
            return false;
          }
        };

        // Valida o código antes de processar
        if (!isJavaValid(inputCode)) {
          return `
            <div style="background-color: #f8f9fa; padding: 8px; border: 1px solid #d0d7de; border-radius: 8px; font-family: 'Courier New', Courier, monospace; font-size: 14px; line-height: 1.5; color: #333;">
              <p style="color: #d32f2f; font-weight: bold; margin: 0;">
                Erro: Código Java não está estruturado corretamente.
              </p>
              <p style="margin: 8px 0 0;">
                Por favor, corrija os problemas estruturais antes de tentar novamente.<br/>
                <a href="/code-analysis" style="color: #1d4f91; text-decoration: underline;">
                  Clique aqui para analisar e corrigir o código.
                </a>
              </p>
            </div>
          `;
        }

        try {
          // Regex para encontrar métodos Java
          const methodRegex = /public\s+(\w+)\s+(\w+)\((.*?)\)/g;
          const matches = Array.from(inputCode.matchAll(methodRegex));
          const tests = matches.map((match) => {
            const returnType = match[1];
            const methodName = match[2];
            const params = match[3]
              .split(",")
              .map((p) => p.trim())
              .filter(Boolean);

            const paramMocks = params
              .map((_param, index) => `mockParam${index + 1}`)
              .join(", ");

            return `
              @Test
              public void test${
                methodName.charAt(0).toUpperCase() + methodName.slice(1)
              }() {
                ${returnType} result = ${methodName}(${paramMocks});
                assertEquals(/* Esperado */, result);
              }
            `;
          });

          if (matches.length === 0) {
            return `
              import org.junit.jupiter.api.Test;
              import static org.junit.jupiter.api.Assertions.*;
      
              public class SampleTest {
                @Test
                public void noMethodsDetected() {
                  assertTrue(true, "Nenhum método detectado no código fornecido.");
                }
              }
            `;
          }

          return `
            import org.junit.jupiter.api.Test;
            import static org.junit.jupiter.api.Assertions.*;
      
            public class GeneratedTests {
              ${tests.join("\n")}
            }
          `;
        } catch (error) {
          return "Erro ao gerar testes unitários.";
        }
      }

      case "C#": {
        // Validação e geração para C#
        const isCSharpValid = (code) => {
          try {
            // Verifica se há uma classe
            const hasClass = /class\s+\w+\s*\{/.test(code);
            // Verifica se há métodos
            const hasMethods =
              /(?:public\s+)?(?:static\s+)?(?:\w+\s+)?(\w+)\s*\((.*?)\)\s*{/.test(
                code
              );
            // Verifica se as chaves estão balanceadas
            const openBraces = (code.match(/{/g) || []).length;
            const closeBraces = (code.match(/}/g) || []).length;
            const unmatchedBraces = openBraces !== closeBraces;

            // Retorna falso se o código não atender às regras
            if (!hasClass || !hasMethods || unmatchedBraces) {
              return false;
            }
            return true; // Código válido
          } catch (error) {
            return false;
          }
        };

        if (!isCSharpValid(inputCode)) {
          return `
                <div style="background-color: #f8f9fa; padding: 8px; border: 1px solid #d0d7de; border-radius: 8px; font-family: 'Courier New', Courier, monospace; font-size: 14px; line-height: 1.5; color: #333;">
                    <p style="color: #d32f2f; font-weight: bold; margin: 0;">
                        Erro: Código C# não está estruturado corretamente.
                    </p>
                    <p style="margin: 8px 0 0;">
                        Por favor, corrija os problemas estruturais antes de tentar novamente.<br/>
                        <a href="/code-analysis" style="color: #1d4f91; text-decoration: underline;">
                            Clique aqui para analisar e corrigir o código.
                        </a>
                    </p>
                </div>
            `;
        }

        try {
          const functionRegex =
            /(?:public\s+)?(?:static\s+)?(?:\w+\s+)?(\w+)\s*\((.*?)\)\s*{/g;
          const matches = Array.from(inputCode.matchAll(functionRegex));
          const tests = matches.map((match) => {
            const functionName = match[1];
            const params = match[2]
              .split(",")
              .map((p) => p.trim().split(" ").pop())
              .filter(Boolean);

            const paramMocks = params
              .map((param, index) => `mockParam${index + 1}`)
              .join(", ");

            return `
                    [Test]
                    public void Test${
                      functionName.charAt(0).toUpperCase() +
                      functionName.slice(1)
                    }() {
                        var result = ${functionName}(${paramMocks});
                        Assert.AreEqual(/* Esperado */, result);
                    }
                `;
          });

          if (matches.length === 0) {
            return `
                    using NUnit.Framework;
    
                    namespace GeneratedTests {
                        [TestFixture]
                        public class Tests {
                            [Test]
                            public void TestPlaceholder() {
                                Assert.IsTrue(true, "Nenhuma função detectada no código fornecido.");
                            }
                        }
                    }
                `;
          }

          return `
                using NUnit.Framework;
    
                namespace GeneratedTests {
                    [TestFixture]
                    public class Tests {
                        ${tests.join("\n")}
                    }
                }
            `;
        } catch (error) {
          return "Erro ao gerar testes unitários.";
        }
      }
    }
  };

  const typeEffect = (text) => {
    setResult("");

    // Para textos grandes, exibe instantaneamente para evitar espera de minutos
    if (text.length > 800) {
      setResult(text);
      return;
    }

    setIsTyping(true);
    let index = 0;
    const interval = setInterval(() => {
      if (index < text.length) {
        setResult((prev) => {
          const updatedText = prev + text[index];
          if (resultBoxRef.current) {
            resultBoxRef.current.scrollTop = resultBoxRef.current.scrollHeight;
          }
          return updatedText;
        });
        index++;
      } else {
        setIsTyping(false);
        clearInterval(interval);
      }
    }, 10);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(result).then(() => {
      setCopyMessage("Código copiado!");
      setTimeout(() => setCopyMessage(""), 2000);
    });
  };

  const handleDownload = () => {
    if (!result || isError) return;
    const blob = new Blob([result], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = suggestedFileName || "tests.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Container
      sx={{
        backgroundColor: darkMode ? "#121212" : "#ffffff", // Fundo condicional
        color: darkMode ? "#ffffff" : "#000000", // Cor condicional
        borderRadius: "8px",
        padding: "16px",
        minHeight: "100vh", // Garante que a altura cubra toda a tela
        display: "flex",
        flexDirection: "column", // Organiza os elementos
        justifyContent: "space-between", // Espaçamento entre conteúdo e footer
      }}
    >
      <Paper
        elevation={2}
        sx={{
          padding: "16px",
          marginBottom: "24px",
          backgroundColor: darkMode ? "#1d1d1d" : "#f9f9f9",
          color: darkMode ? "#ffffff" : "#000000",
          boxShadow: "none", // Remove bordas elevadas
        }}
      >
        <Typography variant="h6" gutterBottom>
          Bem-vindo à plataforma de Testes Unitários da CodeGuardian
        </Typography>
        <Typography variant="body1" gutterBottom>
          Transforme código em testes unitários de forma automatizada, eficiente
          e alinhada com as melhores práticas do mercado.
        </Typography>
        <Typography variant="body2" gutterBottom>
          Com suporte a linguagens amplamente utilizadas, como{" "}
          <strong>JavaScript</strong>, <strong>Python</strong>,{" "}
          <strong>Java</strong> e <strong>C#</strong>, você poderá gerar scripts
          prontos para serem executados, reduzindo o tempo de desenvolvimento e
          garantindo a qualidade do seu software.
        </Typography>
        <Typography variant="body2" gutterBottom>
          <strong>Status Atual:</strong> Suporte para JavaScript, Python, Java e
          C#. Fique atento para futuras atualizações e suporte a novas
          linguagens.
        </Typography>
      </Paper>

      {/* Seção de Informações Detalhadas */}
      <Accordion
        sx={{
          backgroundColor: darkMode ? "#1d1d1d" : "#f9f9f9",
          color: darkMode ? "#ffffff" : "#000000",
          boxShadow: "none", // Remove sombra
          border: darkMode ? "1px solid #333" : "1px solid #ccc", // Adiciona borda
        }}
      >
        <AccordionSummary
          expandIcon={
            <ExpandMoreIcon sx={{ color: darkMode ? "#ffffff" : "#000000" }} />
          }
          aria-controls="setup-content"
          id="setup-header"
        >
          <Typography variant="subtitle1">
            Instruções para {language}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>{instructions[language]}</AccordionDetails>
      </Accordion>

      <FormControl
        fullWidth
        margin="normal"
        variant="outlined"
        sx={{
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderWidth: "2px",
              borderColor: darkMode ? "#ffffff" : "#1d4f91",
            },
            "&:hover fieldset": {
              borderColor: darkMode ? "#90caf9" : "#1d4f91",
            },
            "&.Mui-focused fieldset": {
              borderColor: darkMode ? "#90caf9" : "#1d4f91",
              borderWidth: "2px",
            },
          },
          "& .MuiInputLabel-root": {
            backgroundColor: darkMode ? "#121212" : "#ffffff",
            color: darkMode ? "#ffffff" : "#000000",
          },
        }}
      >
        <InputLabel id="language-select-label">Linguagem</InputLabel>
        <Select
          labelId="language-select-label"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          sx={{
            backgroundColor: darkMode ? "#1d1d1d" : "#ffffff",
            color: darkMode ? "#ffffff" : "#000000",
          }}
        >
          <MenuItem value="JavaScript">JavaScript</MenuItem>
          <MenuItem value="Python">Python</MenuItem>
          <MenuItem value="Java">Java</MenuItem>
          <MenuItem value="C#">C#</MenuItem>
        </Select>
      </FormControl>

      <Box mt={2}>
        <Typography variant="h6">Insira o Código:</Typography>
        <CodeMirror
          value={code}
          options={{
            mode:
              language === "JavaScript"
                ? "javascript"
                : language === "Python"
                ? "python"
                : "text/x-java", // Para Java e C#
            theme: darkMode ? "dracula" : "material", // Tema escuro/claro
            lineNumbers: true,
          }}
          onBeforeChange={(editor, data, value) => setCode(value)}
          style={{
            backgroundColor: darkMode ? "#121212" : "#ffffff", // Fundo condicional
            color: darkMode ? "#ffffff" : "#000000", // Cor condicional
            borderRadius: "8px", // Bordas arredondadas
            overflow: "hidden", // Remove overflow visual
          }}
        />
      </Box>

      <Button
        variant="contained"
        color="primary"
        onClick={handleConversion}
        disabled={isTyping || isLoading}
        sx={{
          mt: 2,
          backgroundColor: darkMode ? "#90caf9" : "#1d4f91",
          color: darkMode ? "#000000" : "#ffffff",
        }}
      >
        {isLoading ? "Aguardando IA..." : isTyping ? "Gerando Testes..." : "Gerar Testes"}
      </Button>

      <Box mt={4} sx={{ position: "relative" }}>
        <Typography variant="h6">Teste Gerado:</Typography>
        {suggestedFileName && (
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              color: darkMode ? "#90caf9" : "#1d4f91",
              fontWeight: "bold",
            }}
          >
            💾 Salve como:{" "}
            <code style={{ backgroundColor: darkMode ? "#333" : "#eef", padding: "2px 6px", borderRadius: 4 }}>
              {suggestedFileName}
            </code>
          </Typography>
        )}
        <Paper
          elevation={3}
          className="result-box"
          sx={{
            padding: "16px",
            backgroundColor: darkMode ? "#1d1d1d" : "#f9f9f9",
            color: darkMode ? "#ffffff" : "#000000",
            border: darkMode ? "1px solid #333" : "1px solid #ccc",
            maxHeight: "65vh",
            overflowY: "auto",
          }}
          ref={resultRef}
        >
          {isError ? (
            <div
              dangerouslySetInnerHTML={{ __html: result }}
              style={{ color: "#333", lineHeight: "1.5" }}
            />
          ) : (
            <pre
              style={{
                margin: 0,
                whiteSpace: "pre-wrap",
                wordWrap: "break-word",
                fontFamily: "'Courier New', Courier, monospace",
                fontSize: "13px",
                lineHeight: "1.6",
              }}
            >
              {result}
            </pre>
          )}
          <Box sx={{ position: "absolute", top: "8px", right: "8px", display: "flex", gap: 0.5 }}>
            {!isError && result && suggestedFileName && (
              <IconButton
                onClick={handleDownload}
                title={`Baixar como ${suggestedFileName}`}
                sx={{
                  backgroundColor: darkMode ? "#4caf50" : "#2e7d32",
                  color: "#ffffff",
                  width: 32,
                  height: 32,
                }}
              >
                <Typography variant="caption" sx={{ color: "white", fontSize: "10px", fontWeight: "bold" }}>DL</Typography>
              </IconButton>
            )}
            <IconButton
              onClick={handleCopy}
              sx={{
                backgroundColor: darkMode ? "#ffffff" : "#1d4f91",
                color: darkMode ? "#1d1d1d" : "#ffffff",
                width: 32,
                height: 32,
              }}
            >
              {copyMessage ? (
                <Typography variant="caption" sx={{ color: "white", fontSize: "10px" }}>
                  {copyMessage}
                </Typography>
              ) : (
                <ContentCopyIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default UnitTests;
